import clsx from 'clsx'
import { useMemo } from 'react'

import { FvButton, Icon, type IconProps } from '@fv/client-components'
import type { FullShipment, Quote } from '@fv/client-types'
import type { Currency, ExchangeRateMap } from '@fv/models'
import type { EquipmentType } from '@fv/models/equipment'

import { ChargeList } from '@/features/charges/ChargeList'
import { ChargesSlider } from '@/features/charges/ChargesSlider'
import { ChargesToggle } from '@/features/charges/ChargesToggle'
import type { ChargeListQuote } from '@/features/charges/types'
import { useChargeList } from '@/features/charges/useChargeList'
import { useTypeNames } from '@/hooks/settings'
import type { QuoteRequestResponse } from '@/hooks/shipments/useQuoteRequestResponses'
import { convertCurrency } from '@/hooks/useExchangeRate'

import { DividedContent } from './DividedContent'
import CheckboxField from './inputs/CheckboxField'
import type { ListStatus } from './QuoteRequest/QuoteRequestDetail'
import { ShipperTooltip } from './ShipperTooltip'
import { Price } from './shared/Price'

type MethodIcons = {
  api: IconProps['icon']
  email: IconProps['icon']
  manual: IconProps['icon']
  upload: IconProps['icon']
}

type MethodLabels = Record<Quote['method'], string>

const methodIcons: MethodIcons = {
  api: 'server',
  email: 'envelope',
  manual: 'user',
  upload: 'file-contract',
}

const methodLabels: MethodLabels = {
  api: 'API rate',
  email: 'Email rate',
  manual: 'Manual rate',
  upload: 'Contracted rate',
}

type ResponseRowProps = {
  data: QuoteRequestResponse
  displayCurrency: Currency | null
  equipmentType: EquipmentType
  exchangeRates?: ExchangeRateMap
  filter: ListStatus
  handleChange: () => void
  handleMessagesClick: () => void
  handleRowClick: () => void
  isActive: boolean
  isAwarded: boolean
  isDisabled: boolean
  isSelected: boolean
  load: FullShipment
}

const ResponseRow = ({
  data,
  displayCurrency,
  equipmentType,
  exchangeRates,
  filter,
  handleChange,
  handleMessagesClick,
  handleRowClick,
  isActive,
  isAwarded,
  isDisabled,
  isSelected,
  load,
}: ResponseRowProps) => {
  const { amount, currency, id, isProcessing, method, name, quoteNum, status } =
    data
  const { equipmentName: getEquipmentName } = useTypeNames()
  const equipmentName = getEquipmentName(data.equipmentType ?? equipmentType)

  const quoteCurrency = currency ?? 'usd'
  const converted =
    displayCurrency && exchangeRates && amount
      ? convertCurrency(amount, quoteCurrency, displayCurrency, exchangeRates)
      : { amount, currency: quoteCurrency }

  const showBidInfo = !(
    method === 'api' &&
    (isProcessing || status === 'error')
  )

  const chargeQuote = useMemo<ChargeListQuote>(
    () => ({
      _id: data.id,
      charges: data.charges,
      currency: quoteCurrency,
      amount: data.amount,
      method: data.method,
      isSpot: data.isSpot,
      providerName: data.providerName,
      source: data.source,
      status: data.status,
    }),
    [data, quoteCurrency],
  )

  const chargeList = useChargeList(chargeQuote)
  const showChargesToggle = !!data.charges?.length || chargeList.canEdit

  return (
    <>
      <div
        className={clsx(
          'flex items-center border-t border-b  py-2 flex-wrap [&+&]:-mt-px',
          isActive ? 'border-fv-orange-bright' : 'border-fv-gray',
          {
            'relative z-10': isActive,
            flash: method === 'manual' && isActive,
          },
        )}
        onClick={handleRowClick}
      >
        <div className="flex-1 overflow-hidden">
          {filter === 'active' && !isProcessing && status !== 'error' && (
            <CheckboxField
              checked={isSelected}
              disabled={isDisabled || !amount}
              label={isAwarded ? `${name} / Awarded` : name}
              name={id}
              onChange={handleChange}
            />
          )}
          {filter === 'active' && isProcessing && (
            <>
              <Icon icon="sync" className="spin text-fv-gray-dark" />
              <span>
                {method === 'api' && 'Fetching rates from '}
                {name}
              </span>
            </>
          )}
          {filter === 'active' && status === 'error' && (
            <>
              <Icon icon="exclamation-triangle" className="text-fv-gray-dark" />
              <span>
                {name} did not return a rate.{' '}
                <a className="ml-2" href="#" onClick={e => e.preventDefault()}>
                  <Icon icon="info-circle" />
                  <span>See why</span>
                </a>
              </span>
            </>
          )}
          {filter !== 'active' && name}
        </div>

        {showBidInfo && (
          <div className="flex items-center">
            <DividedContent>
              <FvButton
                className="px-4 py-2"
                icon="comment-alt-lines"
                onClick={() => {
                  handleRowClick()
                  handleMessagesClick()
                }}
              />

              <ShipperTooltip label={method ? methodLabels[method] : ''}>
                <div>
                  <Icon
                    className="text-fv-gray-dark"
                    icon={method ? methodIcons[method] : 'envelope'}
                  />
                </div>
              </ShipperTooltip>

              {method === 'upload' && quoteNum && <div>#{quoteNum}</div>}

              <div>{equipmentName}</div>

              {showChargesToggle && (
                <div>
                  <ChargesToggle
                    chargeState={chargeList}
                    onClick={e => {
                      e.stopPropagation()
                      chargeList.toggleViewing()
                    }}
                  />
                </div>
              )}

              {Boolean(amount) && status !== 'error' && (
                <Price
                  amount={converted.amount}
                  currency={converted.currency}
                />
              )}
            </DividedContent>
          </div>
        )}

        {chargeList.isViewing && (
          <div className="w-full" onClick={e => e.stopPropagation()}>
            <ChargeList
              chargeState={chargeList}
              load={load}
              quote={chargeQuote}
            />
          </div>
        )}
      </div>

      <ChargesSlider chargeState={chargeList} load={load} />
    </>
  )
}

export default ResponseRow
